/* Store locator — storefront runtime.
 *
 * Ported from the GoHerb handover, with three deliberate departures:
 *
 *  1. NO DATA FETCH. The handover asked an App Proxy for its locations on
 *     every load. A custom-distribution app installs on ONE store, so there
 *     is no proxy to ask across a fleet of twenty. The outlets are inlined by
 *     Liquid into a JSON script tag instead — which also means the addresses
 *     are in the HTML, so they are crawlable, and a theme rollback rolls the
 *     data back with it.
 *
 *  2. NO GEOCODER. The handover billed a Google geocoding call on every
 *     shopper search. Search here runs against a places index built from the
 *     store's own outlets at sync time, so typing "Kuching" pans to where
 *     this store's Kuching outlets actually are — no API call, nothing to
 *     rate-limit, and it cannot disagree with the catalogue.
 *
 *  3. NO innerHTML. Retailer names and addresses are untrusted text from a
 *     CSV, rendered onto client storefronts. Everything reaches the DOM
 *     through textContent, and el() throws on an `html` prop — the same rule
 *     the dashboard runs under, for the same reason.
 *
 * The Maps library is a few hundred KB, so it loads only when the map scrolls
 * into view. The list works, and is complete, before any of that arrives —
 * and if the key is missing, wrong or out of quota, the list still works.
 */
(function () {
  "use strict";

  /* ---------------------------------------------------------------- */
  /* DOM                                                               */
  /* ---------------------------------------------------------------- */

  function el(tag, props, children) {
    var node = document.createElement(tag);
    var p = props || {};
    for (var k in p) {
      if (!Object.prototype.hasOwnProperty.call(p, k)) continue;
      if (k === "html") throw new Error("el(): refusing an html prop");
      if (k === "text") node.textContent = p[k];
      else if (k === "class") node.className = p[k];
      else if (k === "style") node.setAttribute("style", p[k]);
      else if (k.indexOf("on") === 0) node.addEventListener(k.slice(2), p[k]);
      else if (p[k] === true) node.setAttribute(k, "");
      else if (p[k] !== false && p[k] != null) node.setAttribute(k, p[k]);
    }
    var kids = children || [];
    for (var i = 0; i < kids.length; i++) if (kids[i]) node.appendChild(kids[i]);
    return node;
  }

  function clear(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  /* ---------------------------------------------------------------- */
  /* Geometry                                                          */
  /* ---------------------------------------------------------------- */

  var EARTH_KM = 6371;

  /** Haversine, ported from the handover's geocode.server.ts. */
  function distanceKm(aLat, aLng, bLat, bLng) {
    var toRad = function (d) { return (d * Math.PI) / 180; };
    var dLat = toRad(bLat - aLat);
    var dLng = toRad(bLng - aLng);
    var h =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(toRad(aLat)) * Math.cos(toRad(bLat));
    return 2 * EARTH_KM * Math.asin(Math.min(1, Math.sqrt(h)));
  }

  /** Directions open in whatever map app the visitor has. This is a plain
   *  URL scheme — no key, no account, no billing, on any provider. */
  function directionsUrl(loc) {
    return "https://www.google.com/maps/dir/?api=1&destination=" +
      encodeURIComponent(loc.lat + "," + loc.lng);
  }

  /** Waze deep link — the navigation app Malaysia actually uses. Like the
   *  Google directions URL this is a free scheme: no key, no billing. */
  function wazeUrl(loc) {
    return "https://www.waze.com/ul?ll=" + encodeURIComponent(loc.lat + "," + loc.lng) + "&navigate=yes";
  }

  /** Only ever render an image from an http(s) URL. The importer validates
   *  this too; rendering is the wrong place to trust that. */
  function safeImage(url) {
    return typeof url === "string" && /^https?:\/\//i.test(url) ? url : "";
  }

  function pinSvg(color) {
    return "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(
      "<svg xmlns='http://www.w3.org/2000/svg' width='28' height='38' viewBox='0 0 28 38'>" +
        "<path d='M14 0C6.3 0 0 6.3 0 14c0 10.5 14 24 14 24s14-13.5 14-24c0-7.7-6.3-14-14-14z' fill='" +
        color + "'/><circle cx='14' cy='14' r='5' fill='#fff'/></svg>");
  }

  /* ---------------------------------------------------------------- */
  /* Map library, loaded late                                          */
  /* ---------------------------------------------------------------- */

  var VERSION = "sl-24";
  /* ---------------------------------------------------------------- */
  /* Copy                                                             */
  /* ---------------------------------------------------------------- */

  /** Everything the SCRIPT writes into the page, per language.
   *
   * The section's settings hold the copy a merchant chooses — heading,
   * button labels, the WhatsApp message. These are the strings the script
   * composes at runtime: a count, a place name, a distance, a failure. No
   * setting can hold them, which is why Scale Story read "45 locations"
   * under a Chinese heading until this existed.
   *
   * The store picks a language in the section ("Script language"); `auto`
   * follows the storefront locale, which is what a bilingual store wants.
   * English is the default and the fallback, so a store that never touches
   * the setting — and a theme still on the older section, which sends no
   * language at all — reads exactly as it did before.
   *
   * A language is a whole set or it is not here: a half-translated table
   * puts two languages in one sentence, which is worse than one. */
  var COPY = {
    en: {
      directions: "Directions",
      online: "Buy online",
      wa: "WhatsApp us",
      emptyLead: "You can still order online, or message us to find your nearest stockist.",
      readFail: "The store list could not be read.",
      count: function (n) { return n === 1 ? "1 location" : n + " locations"; },
      noneNear: function (term) { return "No stockist near “" + term + "” yet."; },
      noneFilters: "No stockists match those filters.",
      noneIn: function (place) { return "No stockist in " + place + " yet."; },
      noMatch: function (example) {
        return "No stockist matches that. Try a city or state — for example " + example + ".";
      },
      geoUnsupported: "Your browser does not support location sharing.",
      geoFail: "We could not get your location. Try typing a city instead.",
      mapFail: "The map could not load. Every stockist is still listed here.",
      noKey: "Map unavailable — add a Google Maps API key in this section's settings. " +
        "Every stockist is listed either way.",
      cluster: function (n) { return n + " locations"; },
      named: function (n, term, example) {
        return n + (n === 1 ? " stockist" : " stockists") + " with “" + term + "” in the name. " +
          "Add a town for the nearest one — try “" + term + " " + example + "”.";
      },
      website: "Visit website",
      km: function (km) { return "about " + (km < 10 ? km.toFixed(1) : String(Math.round(km))) + " km"; },
      nearest: function (place, name, town, km) {
        return (place ? "No stockist in " + place + " yet — your" : "Your") +
          " nearest is " + name + (town ? " in " + town : "") + ", " + this.km(km) + " away.";
      },
    },
    zh: {
      directions: "导航前往",
      online: "在线购买",
      wa: "WhatsApp 我们",
      emptyLead: "您仍可在线下单，或联系我们查询离您最近的门店。",
      readFail: "门店列表无法读取。",
      count: function (n) { return n + " 家门店"; },
      noneNear: function (term) { return "「" + term + "」附近暂无门店。"; },
      noneFilters: "没有符合筛选条件的门店。",
      noneIn: function (place) { return place + " 暂无门店。"; },
      noMatch: function (example) {
        return "找不到这个地点。请输入城市或州属 — 例如 " + example + "。";
      },
      geoUnsupported: "您的浏览器不支持定位功能。",
      geoFail: "无法获取您的位置，请改为输入城市名称。",
      mapFail: "地图无法载入，所有门店仍列在下方。",
      noKey: "地图暂时无法显示，所有门店仍列在下方。",
      cluster: function (n) { return n + " 家门店"; },
      named: function (n, term, example) {
        return "找到 " + n + " 家名称含「" + term + "」的门店。" +
          "加上城市名可看最近的一家 — 例如「" + term + " " + example + "」。";
      },
      website: "前往网站",
      km: function (km) { return "约 " + (km < 10 ? km.toFixed(1) : String(Math.round(km))) + " 公里"; },
      nearest: function (place, name, town, km) {
        // A space before a Latin shop name: '最近的是BilaBila' reads as one
        // word, and every stockist in this catalogue is named in Latin script.
        return (place ? place + " 暂无门店 — 最近的是 " : "离您最近的是 ") +
          name + (town ? "（" + town + "）" : "") + "，距离" + this.km(km) + "。";
      },
    },
  };

  /** The language this section speaks.
   *
   *  `auto` is resolved by the section, which passes the storefront's own
   *  locale; a Chinese locale code of any flavour (zh, zh-CN, zh-TW) reads
   *  Chinese. Anything else, including a missing attribute, is English. */
  function pickCopy(code) {
    return String(code || "").trim().toLowerCase().indexOf("zh") === 0 ? COPY.zh : COPY.en;
  }

  var libPromise = null;
  // Deep links fire at most once per page load, across all sections and any
  // theme-editor section reloads.
  var deepLinkHandled = false;

  /** One loader per page: several locator sections on one page must not each
   *  append their own copy of the Maps script. */
  function loadGoogleMaps(key) {
    // Google signals a rejected key (referrer, billing, invalid) by calling
    // this global — the script itself loads FINE, so a promise chain never
    // hears about it. Without this hook an auth failure looks like a blank
    // beige canvas and nothing else.
    if (!window.gm_authFailure) {
      window.gm_authFailure = function () {
        document.dispatchEvent(new CustomEvent("sl:gm-auth-failure"));
      };
    }
    if (window.google && window.google.maps) return Promise.resolve(window.google.maps);
    if (libPromise) return libPromise;

    libPromise = new Promise(function (resolve, reject) {
      var s = document.createElement("script");
      // The loading=async bootstrap defines google.maps as an EMPTY object
      // at onload and fetches the real API in a second async step — resolving
      // on onload is exactly how "maps.Map is not a constructor" reached a
      // live storefront. The callback= parameter is the documented signal
      // that the API is actually ready; nothing here trusts onload.
      window.__slMapsReady = function () {
        var g = window.google && window.google.maps;
        if (g && g.Map) { resolve(g); return; }
        if (g && g.importLibrary) {
          Promise.all([g.importLibrary("maps"), g.importLibrary("marker"), g.importLibrary("core")]).then(
            function () { resolve(window.google.maps); },
            function () { reject(new Error("The map classes could not be loaded.")); }
          );
          return;
        }
        reject(new Error("The map library loaded but did not initialise."));
      };
      // No `libraries=geocoding`: search is served from the inlined places
      // index, so the only billable surface is the map itself.
      s.src = "https://maps.googleapis.com/maps/api/js?key=" + encodeURIComponent(key) +
        "&loading=async&callback=__slMapsReady";
      s.async = true;
      s.onerror = function () { reject(new Error("The map could not be loaded.")); };
      document.head.appendChild(s);
    });
    return libPromise;
  }

  /* ---------------------------------------------------------------- */
  /* Locator                                                           */
  /* ---------------------------------------------------------------- */

  function Locator(root) {
    this.root = root;
    var d = root.dataset;
    // Settled before cfg, because four of the labels below default to it.
    var copy = pickCopy(d.lang);
    this.copy = copy;

    this.cfg = {
      mapsKey: (d.mapsKey || "").trim(),
      // One chain can stock a different pack, or a different price, from
      // everywhere else. The catalogue cannot carry that: its rows are one
      // per physical outlet and a Watsons branch stocks several of our
      // brands, each with its own thing to say. So the copy is per STORE
      // and keyed on a catalogue tag.
      noteTag: (d.noteTag || "").trim().toLowerCase(),
      noteText: (d.noteText || "").trim(),
      // Roster handle. One fleet GA4 property holds every store's searches;
      // this is the dimension that tells them apart. From the JSON payload,
      // with the old data-store attribute honoured for a theme still on a
      // section that sets it.
      store: (d.store || "").trim(),
      height: parseInt(d.height, 10) || 480,
      centerLat: parseFloat(d.centerLat),
      centerLng: parseFloat(d.centerLng),
      zoom: parseFloat(d.zoom) || 5,
      unit: d.unit === "mi" ? "mi" : "km",
      cluster: d.cluster !== "false",
      showSearch: d.showSearch !== "false",
      showFilters: d.showFilters !== "false",
      showDirections: d.showDirections !== "false",
      showPhotos: d.showPhotos !== "false",
      // "type" colours each pin by category (tag palette); "single" makes every
      // pin the brand marker colour — the on-brand look.
      pinByType: d.pinStyle !== "single",
      // "type" tints tag chips/pills by category (the payload palette);
      // "brand" makes them all the accent colour, on-brand.
      tagByType: d.tagStyle !== "brand",
      markerColor: d.markerColor || "#2E6B7E",
      clusterColor: d.clusterColor || "#11222D",
      // A store can replace the coloured pin with its own logo, baked into a
      // marker PNG the theme hosts (assets/locator-pin.png, referenced by the
      // section via asset_url). Fleet-generic: the art is per-store and lives
      // on that store's theme; only the URL and its display size arrive here.
      pinImage: (d.pinImage || "").trim(),
      pinImageW: parseInt(d.pinImageW, 10) || 30,
      pinImageH: parseInt(d.pinImageH, 10) || 40,
      directionsLabel: d.directionsLabel || copy.directions,
      // Empty-state escape hatches: a shopper who finds no stockist near them
      // is the highest-intent visitor on the page. Rather than a dead "no
      // results", route them to buy online or message the store.
      onlineUrl: (d.onlineUrl || "").trim(),
      onlineLabel: d.onlineLabel || copy.online,
      waNumber: (d.waNumber || "").replace(/[^\d]/g, ""),
      waText: d.waText || "",
      waLabel: d.waLabel || copy.wa,
      emptyLead: d.emptyLead || copy.emptyLead,
    };

    this.el = {
      form: root.querySelector("[data-sl-form]"),
      input: root.querySelector("[data-sl-input]"),
      locate: root.querySelector("[data-sl-locate]"),
      controls: root.querySelector("[data-sl-controls]"),
      filters: root.querySelector("[data-sl-filters]"),
      chips: root.querySelector("[data-sl-chips]"),
      clear: root.querySelector("[data-sl-clear]"),
      count: root.querySelector("[data-sl-count]"),
      list: root.querySelector("[data-sl-list]"),
      map: root.querySelector("[data-sl-map]"),
      error: root.querySelector("[data-sl-error]"),
      data: root.querySelector("[data-sl-payload]"),
    };

    this.activeTags = [];
    this.searchTag = null;
    // A chain the last search named ("alpro"). Not a tag: tags come from the
    // catalogue and have chips; this is whatever the shopper typed.
    this.searchName = null;
    this.origin = null;
    this.markers = [];
    this.map = null;
    this.info = null;

    this.init();
  }

  Locator.prototype.init = function () {
    var payload;
    try {
      payload = JSON.parse(this.el.data.textContent);
    } catch (e) {
      this.showError(this.copy.readFail);
      return;
    }

    this.data = payload;
    if (!this.cfg.store) this.cfg.store = (payload.store || "").trim();
    this.loadTag(payload.ga4);
    this.all = payload.locations || [];
    this.tagColors = {};
    (payload.tags || []).forEach(function (t) { this.tagColors[t.name] = t.color; }, this);

    this.el.map.style.height = this.cfg.height + "px";
    this.el.controls.hidden = !(this.cfg.showSearch || this.cfg.showFilters);
    this.el.form.hidden = !this.cfg.showSearch;
    this.el.filters.hidden = !this.cfg.showFilters || !(payload.tags || []).length;

    this.renderChips(payload.tags || []);
    this.render();
    this.bind();

    // #o-<external_id> deep-links to one outlet. Consumed at most ONCE per
    // page: the theme editor re-fires shopify:section:load, which builds a
    // fresh Locator that would otherwise keep replaying a stale hash left by
    // an earlier popup open.
    if (!deepLinkHandled) {
      var hash = location.hash.match(/^#o-([A-Za-z0-9._%-]+)$/);
      if (hash) {
        try { this.pendingId = decodeURIComponent(hash[1]); } catch (e) { /* malformed hash */ }
        deepLinkHandled = true;
      }
    }

    this.watchForMap();
  };

  /** The rendered index of the outlet a deep link points at, or -1. */
  Locator.prototype.pendingIndex = function () {
    if (!this.pendingId) return -1;
    var list = this.current || this.all;
    for (var i = 0; i < list.length; i++) {
      if (list[i].id === this.pendingId) return i;
    }
    return -1;
  };

  Locator.prototype.consumePending = function () {
    var i = this.pendingIndex();
    this.pendingId = null;
    if (i !== -1) this.focus(i);
  };

  /** Scroll a deep-linked outlet into view. The map path calls this before
   *  loading the map (so the section is on screen when pins drop); the no-map
   *  path scrolls the matching card, since there is no pin to fly to. */
  Locator.prototype.revealPending = function () {
    var i = this.pendingIndex();
    if (i === -1) return;
    var target = this.cfg.mapsKey ? this.el.map : this.el.list.children[i];
    if (target && target.scrollIntoView) {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    if (!this.cfg.mapsKey && target) target.classList.add("sl-card--focus");
  };

  /** Everything the current filters allow, nearest first when we know where
   *  the visitor is. One function decides what the list and the map show, so
   *  they cannot disagree. */
  Locator.prototype.visible = function () {
    var tags = this.activeTags;
    var origin = this.origin;
    var name = this.searchName;

    var out = this.all.filter(function (loc) {
      if (name && normPlace(loc.name + " " + loc.address).indexOf(name) === -1) return false;
      if (!tags.length) return true;
      for (var i = 0; i < tags.length; i++) {
        if (loc.tags.indexOf(tags[i]) !== -1) return true;
      }
      return false;
    });

    if (origin) {
      out = out.map(function (loc) {
        var copy = {};
        for (var k in loc) copy[k] = loc[k];
        copy.distanceKm = distanceKm(origin.lat, origin.lng, loc.lat, loc.lng);
        return copy;
      }).sort(function (a, b) { return a.distanceKm - b.distanceKm; });
    }

    return out;
  };

  Locator.prototype.render = function () {
    var list = this.visible();
    this.current = list;

    this.el.count.textContent = this.copy.count(list.length);

    clear(this.el.list);
    if (!list.length) {
      this.el.list.appendChild(this.emptyState());
      this.drawMarkers(list);
      return;
    }

    var self = this;
    list.forEach(function (loc, index) {
      self.el.list.appendChild(self.card(loc, index));
    });
    this.drawMarkers(list);
  };

  /** The empty result state — a routing point, not a dead end.
   *
   * The message names what the visitor searched, then offers whatever the
   * store configured: buy online, or message on WhatsApp. Both are optional;
   * with neither set it degrades to a plain "nothing found" line, so a store
   * that wants the old behaviour just leaves the settings blank. */
  Locator.prototype.emptyState = function () {
    var searched = this.origin && this.el.input && this.el.input.value.trim();
    var kids = [
      el("p", { class: "sl-empty__title",
        text: searched
          ? this.copy.noneNear(this.el.input.value.trim())
          : this.copy.noneFilters }),
    ];

    var actions = [];
    if (this.cfg.onlineUrl) {
      actions.push(el("a", { class: "sl-empty__btn sl-empty__btn--primary",
        href: this.cfg.onlineUrl, text: this.cfg.onlineLabel }));
    }
    if (this.cfg.waNumber) {
      var href = "https://wa.me/" + this.cfg.waNumber +
        (this.cfg.waText ? "?text=" + encodeURIComponent(this.cfg.waText) : "");
      actions.push(el("a", { class: "sl-empty__btn", href: href,
        target: "_blank", rel: "noopener", text: this.cfg.waLabel }));
    }
    if (actions.length) {
      kids.push(el("p", { class: "sl-empty__lead", text: this.cfg.emptyLead }));
      kids.push(el("div", { class: "sl-empty__actions" }, actions));
    }
    return el("div", { class: "sl-empty" }, kids);
  };

  Locator.prototype.card = function (loc, index) {
    var self = this;
    var head = [el("h3", { class: "sl-card__name", text: loc.name })];

    if (typeof loc.distanceKm === "number") {
      var value = this.cfg.unit === "mi" ? loc.distanceKm * 0.621371 : loc.distanceKm;
      head.push(el("span", { class: "sl-card__distance", text: value.toFixed(1) + " " + this.cfg.unit }));
    }

    var kids = [];
    if (this.cfg.showPhotos && safeImage(loc.image)) {
      kids.push(el("img", { class: "sl-card__thumb", src: loc.image, alt: "", loading: "lazy" }));
    }
    kids.push(el("div", { class: "sl-card__head" }, head));

    if (loc.tags && loc.tags.length) kids.push(this.tagRow(loc.tags));
    kids.push(el("p", { class: "sl-card__addr", text: loc.address }));
    var cardNote = this.noteFor(loc);
    if (cardNote) kids.push(el("p", { class: "sl-card__note", text: cardNote }));

    if (loc.phone) {
      kids.push(el("p", { class: "sl-card__meta" }, [
        el("a", { href: "tel:" + loc.phone.replace(/[^\d+]/g, ""), text: loc.phone }),
      ]));
    }
    if (loc.hours) kids.push(el("p", { class: "sl-card__hours", text: loc.hours }));
    if (this.cfg.showDirections) {
      kids.push(el("div", { class: "sl-card__links" }, [
        el("a", { class: "sl-card__dir", href: directionsUrl(loc), target: "_blank",
                  rel: "noopener", text: this.cfg.directionsLabel }),
        el("a", { class: "sl-card__dir sl-card__dir--waze", href: wazeUrl(loc), target: "_blank",
                  rel: "noopener", text: "Waze" }),
      ]));
    }

    // The card is clickable, but it CONTAINS links (directions, Waze, tel).
    // Both handlers bail when the event originated on a link, so following a
    // link never also pans the map — and, critically, so a keyboard user
    // pressing Enter on "Waze" actually opens Waze instead of having the
    // card's preventDefault cancel it. A per-anchor stopPropagation missed
    // the keyboard path entirely; a target check covers click and key alike.
    var onLink = function (e) {
      return e.target && e.target.closest && e.target.closest("a");
    };
    var card = el("article", {
      class: "sl-card",
      "data-sl-name": loc.name,
      role: "listitem",
      tabindex: "0",
      onclick: function (e) { if (!onLink(e)) self.focus(index); },
      onkeydown: function (e) {
        if (onLink(e)) return;
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); self.focus(index); }
      },
    }, kids);
    return card;
  };

  /** The colour a tag renders in — its category colour, or the brand accent
   *  when tag_style is "brand". One place so chips and pills always agree. */
  Locator.prototype.tagFill = function (name) {
    if (!this.cfg.tagByType) return this.cfg.markerColor;
    return this.tagColors[name] || this.cfg.markerColor;
  };

  Locator.prototype.tagRow = function (tags) {
    var self = this;
    return el("div", { class: "sl-card__tags" }, tags.map(function (name) {
      return el("span", { class: "sl-tag", style: "--tag:" + self.tagFill(name), text: name });
    }));
  };

  Locator.prototype.renderChips = function (tags) {
    var self = this;
    clear(this.el.chips);

    this.el.chips.appendChild(el("button", {
      type: "button", class: "sl-chip is-active", "data-tag": "",
      onclick: function () { self.toggleTag(""); },
      text: "All",
    }));

    tags.forEach(function (t) {
      self.el.chips.appendChild(el("button", {
        type: "button", class: "sl-chip", "data-tag": t.name,
        onclick: function () { self.toggleTag(t.name); },
      }, [
        el("span", { class: "sl-chip__dot", style: "background:" + self.tagFill(t.name) }),
        el("span", { text: t.name }),
      ]));
    });
  };

  Locator.prototype.toggleTag = function (tag) {
    if (!tag) {
      this.activeTags = [];
      this.searchName = null;
      this.hideError();
    } else {
      var i = this.activeTags.indexOf(tag);
      if (i === -1) this.activeTags.push(tag);
      else this.activeTags.splice(i, 1);
    }

    var self = this;
    var chips = this.el.chips.querySelectorAll(".sl-chip");
    for (var c = 0; c < chips.length; c++) {
      var value = chips[c].getAttribute("data-tag");
      var on = value ? self.activeTags.indexOf(value) !== -1 : self.activeTags.length === 0;
      chips[c].classList.toggle("is-active", on);
    }
    this.el.clear.hidden = this.activeTags.length === 0 && !this.searchName;
    this.render();
  };

  /* ---------------------------------------------------------------- */
  /* Search — against this store's own places, not a geocoder          */
  /* ---------------------------------------------------------------- */

  /**
   * Hand the SHOP's own analytics (GA4 via gtag, or GTM via dataLayer) the one
   * fact the locator is worth measuring: which place a shopper searched for,
   * and whether a stockist was actually near it. The locator stores nothing
   * itself — a search that finds no one nearby is a coverage gap, and that is
   * only visible if someone counts it.
   *
   * Wrapped in try/catch and guarded on every global: analytics is never
   * allowed to break the map or the list.
   */
  /**
   * gtag for the ONE fleet-wide locator property, loaded from the page
   * rather than a Shopify web pixel — a pixel runs in a sandbox this page
   * cannot reach, so the locator's events would otherwise go nowhere. Never
   * a client's own property, whose users and sessions it would inflate.
   *
   * In its own gtag group, so a client theme's own gtag('event') calls (no
   * send_to) never reach it. Skipped when a gtag is already on the page for
   * this id — an older section rendered the loader itself.
   */
  Locator.prototype.loadTag = function (id) {
    if (!id || !/^G-[A-Z0-9]+$/.test(id)) return;
    if (document.querySelector('script[src*="' + id + '"]')) return;
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== "function") {
      window.gtag = function () { window.dataLayer.push(arguments); };
    }
    var s = document.createElement("script");
    s.async = true;
    s.src = "https://www.googletagmanager.com/gtag/js?id=" + encodeURIComponent(id);
    document.head.appendChild(s);
    window.gtag("js", new Date());
    window.gtag("config", id, { groups: "locator" });
  };

  /** The per-store note for this outlet, or null. Tag match is
   *  case-insensitive: the setting is typed by a human, the tag by another. */
  Locator.prototype.noteFor = function (loc) {
    if (!this.cfg.noteTag || !this.cfg.noteText) return null;
    var tags = loc.tags || [];
    for (var i = 0; i < tags.length; i++) {
      if (String(tags[i]).toLowerCase() === this.cfg.noteTag) return this.cfg.noteText;
    }
    return null;
  };

  Locator.prototype.track = function (name, params) {
    try {
      var p = params || {};
      if (this.cfg && this.cfg.store) p.store = this.cfg.store;
      if (typeof window.gtag === "function") {
        // "locator" is the fleet tag's group (see the section); "default" is
        // whatever GA4 the store's own theme runs, if any, so the client sees
        // their searches too. A group with no tag in it is silently skipped.
        var g = { send_to: ["locator", "default"] };
        for (var q in p) if (Object.prototype.hasOwnProperty.call(p, q)) g[q] = p[q];
        window.gtag("event", name, g);
      }
      window.dataLayer = window.dataLayer || [];
      var row = { event: name };
      for (var k in p) if (Object.prototype.hasOwnProperty.call(p, k)) row[k] = p[k];
      window.dataLayer.push(row);
    } catch (e) { /* never let a missing tag break the locator */ }
  };

  /** Km to the closest outlet from the current origin — the number that says
   *  whether a "hit" was actually useful. Null when there is no origin. */
  Locator.prototype.nearestKm = function () {
    if (!this.origin) return null;
    var list = this.all || [], best = null;
    for (var i = 0; i < list.length; i++) {
      var l = list[i];
      if (typeof l.lat !== "number" || typeof l.lng !== "number") continue;
      var d = distanceKm(this.origin.lat, this.origin.lng, l.lat, l.lng);
      if (best === null || d < best) best = d;
    }
    return best === null ? null : Math.round(best * 10) / 10;
  };

  /* What a shopper types is rarely the catalogue's spelling. The index is
   * the outlet export's city and state columns, so it said "Pulau Pinang"
   * while shoppers typed "penang" — the most searched term that found
   * nothing, on six storefronts with outlets in Pulau Pinang. Four things
   * close that gap, all against data already in the payload:
   *
   *   1. normPlace() folds spellings both sides share: case, punctuation,
   *      "Sg"/"Sungai", "Sri"/"Seri", "Bahru"/"Baru", "Wilayah
   *      Persekutuan", "Darul Ta'zim".
   *   2. `aka` on a place (retailers/places.csv) — "penang", "kl", "jb",
   *      "槟城" — and `areas`, places no outlet names (Kota Damansara,
   *      KLCC, Singapore) at their own geocoded point.
   *   3. A known name inside a longer query: "perak ipoh", "seri kembangan,
   *      selangor", "watson kl".
   *   4. One slip — a dropped, doubled, swapped or wrong letter — when the
   *      query is long enough that one letter cannot make it another place.
   *
   * Still no geocoder: nothing here calls anything, so nothing can be billed
   * or rate-limited, and every answer is a place the payload already holds. */

  /** Words that mean the same thing in a Malaysian place name. */
  var SAME_WORD = {
    sg: "sungai", sgai: "sungai", bt: "batu", kg: "kampung", kpg: "kampung",
    tmn: "taman", jln: "jalan", jl: "jalan", bdr: "bandar", bkt: "bukit",
    tg: "tanjung", tanjong: "tanjung", sri: "seri", bahru: "baru", bharu: "baru",
    air: "ayer", mt: "mount",
  };

  /** The fold applied to BOTH sides before any comparison. */
  function normPlace(value) {
    var t = String(value == null ? "" : value).toLowerCase();
    if (t.normalize) t = t.normalize("NFKD").replace(/[\u0300-\u036f]/g, "");
    t = t.replace(/[.,;:'"`\u2018\u2019\u201c\u201d()\[\]{}\/\\&+_#@!?*~<>|=\uff0c\u3001\u3002\uff08\uff09-]+/g, " ");
    // Initials typed with dots are one word once the dots go: "K.L." is kl,
    // "W.P." is wp.
    t = t.replace(/\b([a-z])\s+(?=[a-z]\b)/g, "$1");
    var words = t.split(/\s+/), out = [];
    for (var i = 0; i < words.length; i++) {
      var w = words[i];
      if (!w || w === "malaysia" || w === "wp") continue;
      if (w === "wilayah" && words[i + 1] === "persekutuan") { i++; continue; }
      // A state's honorific: "Darul Ehsan", "Darul Ta'zim" (two words once
      // the apostrophe goes). "Darul" never starts a place a shopper needs.
      if (w === "darul") { i++; if (words[i] === "ta") i++; continue; }
      out.push(SAME_WORD.hasOwnProperty(w) ? SAME_WORD[w] : w);
    }
    return out.join(" ");
  }

  var CJK = /[\u3400-\u9fff]/;

  /** Smaller is broader. A places.csv area is a neighbourhood, a mall or a
   *  town no outlet names, so it is the most specific thing a query holds:
   *  "muar sungai abong" means Sungai Abong, "meru klang" means Meru. */
  var RANK = { state: 1, city: 2, area: 3 };

  /** Every name and alias the payload knows, folded. tier 0 is this store's
   *  own places, 1 the fleet's, 2 places.csv areas: on a tie the store's own
   *  answer wins, since only it has stockists. */
  function searchIndex(data) {
    var out = [];
    function add(list, tier) {
      for (var i = 0; list && i < list.length; i++) {
        var p = list[i];
        if (!p || typeof p.lat !== "number" || typeof p.lng !== "number") continue;
        var names = [p.name].concat(p.aka || []);
        for (var k = 0; k < names.length; k++) {
          var key = normPlace(names[k]);
          if (key) out.push({ key: key, place: p, tier: tier, rank: RANK[p.kind] || 2 });
        }
      }
    }
    add(data.places, 0);
    add(data.elsewhere, 1);
    add(data.areas, 2);
    return out;
  }

  /** Optimal string alignment distance, giving up past `max`. A swap counts
   *  as one slip: "kalng" is one from "klang", not two. */
  function slips(a, b, max) {
    if (Math.abs(a.length - b.length) > max) return max + 1;
    var prev2 = null, prev = [], cur, i, j;
    for (j = 0; j <= b.length; j++) prev.push(j);
    for (i = 1; i <= a.length; i++) {
      cur = [i];
      var low = i;
      for (j = 1; j <= b.length; j++) {
        var cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        var v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
        if (prev2 && j > 1 && a.charAt(i - 1) === b.charAt(j - 2) && a.charAt(i - 2) === b.charAt(j - 1)) {
          v = Math.min(v, prev2[j - 2] + 1);
        }
        cur.push(v);
        if (v < low) low = v;
      }
      if (low > max) return max + 1;
      prev2 = prev;
      prev = cur;
    }
    return prev[b.length];
  }

  /** "watson" or "watsons" for the tag Watsons; the plural either way. */
  function tagFor(tags, words) {
    if (!words) return null;
    for (var i = 0; tags && i < tags.length; i++) {
      var t = normPlace(tags[i].name);
      if (t && (words === t || words + "s" === t || words === t + "s")) return tags[i].name;
    }
    return null;
  }

  /** Where a postcode is: the fleet's outlets that carry it, else those
   *  sharing its first four or three digits ("319--" and "8000" get typed
   *  too). A 3-digit stem is about one town. */
  function postcodePlace(data, digits) {
    var list = data.postcodes || [];
    for (var len = Math.min(digits.length, 5); len >= 3; len--) {
      if (len === 5 && digits.length !== 5) continue;
      var stem = digits.slice(0, len), lat = 0, lng = 0, n = 0;
      for (var i = 0; i < list.length; i++) {
        if (list[i] && String(list[i][0]).indexOf(stem) === 0) { lat += list[i][1]; lng += list[i][2]; n++; }
      }
      if (n) return { name: digits, kind: "postcode", lat: lat / n, lng: lng / n, n: 0 };
    }
    return null;
  }

  /** Every outlet whose name or address holds this text. One is a place to
   *  go; several are a filter. */
  function outletsNamed(locations, q) {
    var out = [];
    for (var i = 0; i < locations.length; i++) {
      if (normPlace(locations[i].name + " " + locations[i].address).indexOf(q) !== -1) out.push(locations[i]);
    }
    return out;
  }

  /**
   * What a search resolves to, against the payload alone — pure, so the
   * smoke test and `locator:places` replay real searches through this exact
   * code. Null when nothing fits. Otherwise:
   *   place  where to measure from ({name, lat, lng, n}); null for a bare tag
   *   own    true when it is one of THIS store's places or outlets
   *   tag    a tag the query named ("watson kl"), or null
   *   how    exact | contains | tag | prefix | substring | outlet | postcode | slip
   */
  function resolveSearch(data, typed) {
    var q = normPlace(typed);
    if (!q) return null;
    var index = searchIndex(data);
    var locations = data.locations || [];
    var i, e, best = null;

    function result(entry, how, rest) {
      return { place: entry.place, own: entry.tier === 0, tag: tagFor(data.tags, rest), how: how };
    }
    function named(entry, how, rest) {
      var r = result(entry, how, rest);
      // "alpro johor": the place is Johor, and the leftover still names
      // shops. Only NAMES here — matching the address would make "jalan"
      // and "taman" filters, and those are in half the addresses in the
      // country.
      if (!r.tag && rest && rest.length >= 3) {
        var rest_hits = 0;
        for (var k = 0; k < locations.length; k++) {
          if (normPlace(locations[k].name).indexOf(rest) !== -1) rest_hits++;
        }
        if (rest_hits) r.name = rest;
      }
      return r;
    }
    function outlet(loc) {
      return { place: { name: loc.name, lat: loc.lat, lng: loc.lng, n: 1 }, own: true, tag: null, how: "outlet" };
    }

    // Digits are a postcode, never a place name. One of this store's outlets
    // carrying it is the best answer, as it always was.
    if (/^[\d ]+$/.test(q)) {
      var digits = q.replace(/ /g, "");
      if (digits.length < 3 || digits.length > 5) return null;
      if (digits.length === 5) {
        for (i = 0; i < locations.length; i++) {
          if ((locations[i].address || "").indexOf(digits) !== -1) return outlet(locations[i]);
        }
      }
      var pc = postcodePlace(data, digits);
      return pc ? { place: pc, own: false, tag: null, how: "postcode" } : null;
    }

    for (i = 0; i < index.length; i++) {
      e = index[i];
      if (e.key === q && (!best || e.tier < best.tier)) best = e;
    }
    if (best) return named(best, "exact", "");

    // One slip from a whole name: "johot bahru", "petaling jays", "kalng".
    // Under five letters one letter makes a different word, so not there.
    // Before the containment step, or "petaling jays" stops at "Petaling".
    if (q.length >= 5) {
      var close = [];
      for (i = 0; i < index.length; i++) {
        if (index[i].key.length >= 4 && slips(q, index[i].key, 1) <= 1) close.push(index[i]);
      }
      close.sort(function (a, b) { return a.tier - b.tier; });
      // Two different places one slip away is a guess, and a wrong guess
      // lands a shopper somewhere they did not mean with no sign of it. Two
      // spellings of one place ("Johor", "Johor Darul Ta'zim") are not.
      var sure = close.length > 0;
      for (i = 1; i < close.length; i++) {
        if (close[i].key !== close[0].key && close[i].place.name !== close[0].place.name) sure = false;
      }
      if (sure) return result(close[0], "slip", "");
    }

    // The most specific name the query holds: an area over a city over a
    // state, then the longer name ("subang jaya" over "kl"), then our own.
    var padded = " " + q + " ";
    for (i = 0; i < index.length; i++) {
      e = index[i];
      var inside = CJK.test(e.key) ? q.indexOf(e.key) !== -1 : padded.indexOf(" " + e.key + " ") !== -1;
      if (!inside) continue;
      if (!best || e.rank > best.rank || (e.rank === best.rank &&
          (e.key.length > best.key.length || (e.key.length === best.key.length && e.tier < best.tier)))) best = e;
    }
    if (best) {
      var rest = CJK.test(best.key) ? "" : padded.replace(" " + best.key + " ", " ").trim();
      return named(best, "contains", rest);
    }

    // No place named: maybe the whole query is a tag ("watsons").
    var onlyTag = tagFor(data.tags, q);
    if (onlyTag) return { place: null, own: true, tag: onlyTag, how: "tag" };

    // What the box always did: the start, then the middle, of a name. Three
    // letters for a prefix and four for a middle, so "kl" never became Klang.
    function partial(tier) {
      var t;
      if (q.length >= 3) for (t = 0; t < index.length; t++) if (index[t].tier === tier && index[t].key.indexOf(q) === 0) return result(index[t], "prefix", "");
      if (q.length >= 4) for (t = 0; t < index.length; t++) if (index[t].tier === tier && index[t].key.indexOf(q) !== -1) return result(index[t], "substring", "");
      return null;
    }
    var hit = partial(0);
    if (hit) return hit;

    // An outlet's name or street: "leo pharmacy", "jalan ipoh".
    if (q.length >= 3) {
      var hits = outletsNamed(locations, q);
      if (hits.length === 1) return outlet(hits[0]);
      // A chain, or a word many addresses share. Naming ONE of them was the
      // old behaviour and it answered a question nobody asked: "alpro" went
      // to whichever Alpro sat first in the list — Arau, in Perlis, for a
      // shopper in Johor — and the list then sorted around Perlis with
      // nothing on the page to say so. 20 branches is a filter, not a place.
      if (hits.length > 1) {
        return { place: null, own: true, tag: null, name: q, how: "name" };
      }
    }
    hit = partial(1) || partial(2);
    if (hit) return hit;

    return null;
  }

  /** A search that lands on a place with no stockist IN it, but one this
   *  close, is answered — not told "no stockist in Bangsar yet" about a
   *  pharmacy two streets away. */
  var NEAR_KM = 3;

  /** The chain filter explains itself wherever it is on, because the
   *  "Clear filters" button lives in a row some stores' CSS hides, and a
   *  list of 20 out of 87 with nothing to say why is the bug this whole
   *  change is about. A notice already up (the nearest-stockist line) is
   *  more useful, so it wins. */
  Locator.prototype.noticeIfFiltered = function () {
    if (!this.searchName || !this.el.error.hidden) return;
    var places = this.data.places || [];
    this.showError(
      this.copy.named((this.current || []).length, this.searchName, places[0] ? places[0].name : "KL"),
      "notice",
    );
  };

  Locator.prototype.search = function (query) {
    var raw = (query || "").trim();
    // An empty box is "show me everything again" — on every store, whatever
    // its CSS does to the filter row. The native ✕ of a type=search input
    // reaches this too (see bind()).
    if (!raw) {
      this.searchName = null;
      if (this.searchTag && this.activeTags.indexOf(this.searchTag) !== -1) this.toggleTag(this.searchTag);
      this.searchTag = null;
      this.origin = null;
      this.el.clear.hidden = this.activeTags.length === 0;
      this.hideError();
      this.render();
      return;
    }

    // Held in a local because the no-match message below names one as the
    // worked example — hoisting the lookup into findPlace() once left that
    // line referencing a variable that no longer existed, and every search
    // matching nothing anywhere threw instead of explaining itself (sl-18,
    // two days live). scripts/smoke-locator.ts replays searches through
    // resolveSearch(); this method is still checked in a real browser.
    var places = this.data.places || [];
    var found = resolveSearch(this.data, raw);

    // "watsons", "watson kl": the chip does the filtering, where the shopper
    // can see it and clear it; the place, if any, is searched as usual. A
    // chip a search turned on, the next search turns off again unless it
    // names the tag too — "watson" then "penang" means any stockist in
    // Penang, not Watsons only. A chip the shopper clicked is theirs.
    var tag = found ? found.tag : null;
    if (this.searchTag && this.searchTag !== tag && this.activeTags.indexOf(this.searchTag) !== -1) {
      this.toggleTag(this.searchTag);
    }
    if (tag && (this.activeTags.length !== 1 || this.activeTags[0] !== tag)) {
      this.activeTags = [];
      this.toggleTag(tag);
    }
    this.searchTag = tag;

    // A chain filter lives exactly as long as the search that set it, and
    // the Clear button takes it off.
    this.searchName = found && found.name ? found.name : null;
    this.el.clear.hidden = this.activeTags.length === 0 && !this.searchName;

    if (found && !found.place) {
      this.origin = null;
      this.render();
      var shown = (this.current || []).length;
      this.hideError();
      this.noticeIfFiltered();
      this.lastSearch = { search_term: raw, matched: true, matched_place: found.tag || found.name };
      this.track("locator_search", {
        search_term: raw,
        matched: true,
        matched_place: found.tag || found.name,
        result_count: shown,
      });
      return;
    }

    /* Nothing of THIS store's is there — but the fleet's catalogue, an area
     * from places.csv or a postcode still gives a coordinate, and "your
     * nearest is 85 km away in Temerloh" beats "no match" for a shopper who
     * can still drive or WhatsApp. The list sorts by distance from it. */
    if (found && !found.own && this.all.length) {
      var away = found.place;
      this.origin = { lat: away.lat, lng: away.lng };
      this.render();
      var near = this.current[0];
      var landed = !!near && typeof near.distanceKm === "number" && near.distanceKm <= NEAR_KM;
      if (landed) this.hideError();
      else this.showError(this.nearestLine(away.name) || this.copy.noneIn(away.name), "notice");
      this.noticeIfFiltered();
      if (near) this.fitTo(this.origin, near);
      this.lastSearch = { search_term: raw, matched: landed, matched_place: away.name };
      // matched stays false unless a stockist is within NEAR_KM: otherwise
      // there is none where they asked, and nearest_km says how bad that is.
      this.track("locator_search", {
        search_term: raw,
        matched: landed,
        matched_place: away.name,
        result_count: this.current.length,
        nearest_km: this.nearestKm(),
      });
      return;
    }

    if (!found || !found.own) {
      this.showError(this.copy.noMatch(places[0] ? places[0].name : "Selangor"));
      // A search that matched nothing is the most useful row in the report:
      // it is demand pointing at a place this store does not cover.
      this.lastSearch = { search_term: raw, matched: false };
      this.track("locator_search", { search_term: raw, matched: false, result_count: 0 });
      return;
    }

    var hit = found.place;
    this.hideError();
    this.origin = { lat: hit.lat, lng: hit.lng };
    this.render();
    this.noticeIfFiltered();
    this.flyTo(hit.lat, hit.lng, hit.n > 3 ? 9 : 12);
    /* search_term is what they TYPED ("kl", "K.L.", "kuala lumpur");
     * matched_place is what it RESOLVED to, and that name comes from this
     * store's own places index — so it joins straight back to a coordinate
     * we already hold. Without it every spelling is a separate, unmappable
     * row. Still no visitor coordinates: the place is public, they are not. */
    this.lastSearch = { search_term: raw, matched: true, matched_place: hit.name };
    this.track("locator_search", {
      search_term: raw,
      matched: true,
      matched_place: hit.name,
      result_count: (this.current || this.all || []).length,
      nearest_km: this.nearestKm(),
    });
  };

  Locator.prototype.locateVisitor = function (silent) {
    var self = this;
    if (!navigator.geolocation) {
      if (!silent) this.showError(this.copy.geoUnsupported);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      function (pos) {
        self.hideError();
        self.origin = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        self.render();
        var km = self.nearestKm();
        /* 50 km is the rescue bar's own "another city away" line. Under it
         * the sorted list already answers; over it, say so in words. */
        if (km !== null && km > 50 && self.current[0]) {
          self.showError(self.nearestLine(null), "notice");
          self.fitTo(self.origin, self.current[0]);
        } else {
          self.flyTo(self.origin.lat, self.origin.lng, 10);
        }
        // Deliberately NO coordinates: how far the nearest stockist is answers
        // the coverage question without sending anyone's location anywhere.
        self.lastSearch = { search_term: "", matched: true };
        self.track("locator_locate", {
          matched: true,
          result_count: (self.current || self.all || []).length,
          nearest_km: self.nearestKm(),
        });
      },
      function () {
        if (!silent) self.showError(self.copy.geoFail);
      },
      { timeout: 8000, maximumAge: 300000 },
    );
  };

  Locator.prototype.bind = function () {
    var self = this;
    this.el.form.addEventListener("submit", function (e) {
      e.preventDefault();
      self.search(self.el.input.value);
    });
    // A type=search input draws its own ✕ in WebKit and Chrome, and that
    // fires `search`, not `submit`. It is the only clear affordance on a
    // store whose CSS hides the filter row.
    this.el.input.addEventListener("search", function () {
      if (!self.el.input.value.trim()) self.search("");
    });
    this.el.locate.addEventListener("click", function () { self.locateVisitor(false); });
    this.el.clear.addEventListener("click", function () { self.toggleTag(""); });

    /* Two clicks say more than any search does. Directions = which outlet a
     * shopper actually set off for, so an outlet nobody routes to is a
     * candidate to drop and one everyone routes to is where the next one
     * should be. A WhatsApp link inside the locator = the per-store rescue
     * bar ("no stockist near you?"): its click is a lost walk-in sale, and
     * the search that preceded it says where. Delegated on the root, so the
     * rescue bar — a per-store script the fleet never reaches into — needs
     * no contract beyond "a wa.me link inside the locator". */
    this.root.addEventListener("click", function (e) {
      var a = e.target && e.target.closest ? e.target.closest("a[href]") : null;
      if (!a) return;
      var ctx = self.lastSearch || {};
      if (a.classList.contains("sl-card__dir") || a.classList.contains("sl-popup__dir")) {
        var card = a.closest("[data-sl-name]");
        self.track("locator_directions", {
          matched_place: card ? card.getAttribute("data-sl-name") : "",
          search_term: ctx.search_term || "",
          matched: ctx.matched !== false,
          nearest_km: self.nearestKm(),
          result_count: /waze/.test(a.className) ? 1 : 0,
        });
      } else if (/^https:\/\/(wa\.me|api\.whatsapp\.com)\//.test(a.href)) {
        self.track("locator_rescue", {
          search_term: ctx.search_term || "",
          matched: ctx.matched === true,
          matched_place: ctx.matched_place || "",
          nearest_km: self.nearestKm(),
          result_count: (self.current || self.all || []).length,
        });
      }
    });
  };

  /* ---------------------------------------------------------------- */
  /* Map                                                               */
  /* ---------------------------------------------------------------- */

  /** The library is a third of a megabyte; it waits until the map is nearly
   *  on screen. A visitor who never scrolls that far never pays for it. */
  Locator.prototype.mapFail = function (detail) {
    this.lastError = detail;
    if (window.console && console.error) console.error("[store-locator]", detail);
    this.el.map.classList.remove("is-loading");
    this.el.map.classList.add("sl-map--disabled");
    clear(this.el.map);
    this.el.map.appendChild(el("p", {
      class: "sl-map__note",
      text: this.copy.mapFail,
    }));
  };

  Locator.prototype.watchForMap = function () {
    var self = this;
    document.addEventListener("sl:gm-auth-failure", function () {
      self.mapFail("Google rejected the map key for this site — check the key's referrer list and that billing is active on the Cloud project.");
    });
    if (!this.cfg.mapsKey) {
      this.el.map.classList.add("sl-map--disabled");
      clear(this.el.map);
      this.el.map.appendChild(el("p", {
        class: "sl-map__note",
        text: this.copy.noKey,
      }));
      // No map, but a shared link should still land the visitor on the
      // outlet's card rather than the top of a long page.
      this.revealPending();
      return;
    }

    // A deep link must NOT wait to be scrolled into view. The visitor arrived
    // FOR the pin, but the page loads at the top and the section may sit far
    // below the fold, where the observer would never fire and the map would
    // never load. Bring the section into view and load the map now.
    if (this.pendingIndex() !== -1) {
      this.revealPending();
      return this.initMap();
    }

    if (!("IntersectionObserver" in window)) return this.initMap();

    var started = false;
    var start = function () {
      if (started) return;
      started = true;
      io.disconnect();
      self.initMap();
    };
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) { start(); return; }
      }
    }, { rootMargin: "400px" });
    io.observe(this.el.map);

    // A hidden element never intersects. The placeholder in the section
    // defeats the known cause (Dawn's div:empty rule), but an unknown theme
    // can hide a bare div some other way — after a few seconds, load the map
    // regardless rather than silently never trying.
    setTimeout(function () {
      if (!started && self.el.map.offsetHeight === 0) start();
    }, 3000);
  };

  Locator.prototype.initMap = function () {
    var self = this;
    this.el.map.classList.add("is-loading");

    return loadGoogleMaps(this.cfg.mapsKey).then(function (maps) {
      var center = self.startCenter();

      self.maps = maps;
      self.map = new maps.Map(self.el.map, {
        center: { lat: center.lat, lng: center.lng },
        zoom: center.zoom,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
      });
      self.info = new maps.InfoWindow();

      maps.event.addListenerOnce(self.map, "idle", function () {
        self.el.map.classList.remove("is-loading");
      });
      self.map.addListener("zoom_changed", function () {
        self.drawMarkers(self.current || self.all);
      });

      self.drawMarkers(self.current || self.all);
      self.consumePending();
    }).catch(function (err) {
      // A missing key, a blocked script or an exhausted quota must not take
      // the list down with it — the addresses are the point; the map is the
      // garnish. The shopper gets one sentence; the detail goes to the
      // console, because Google's error strings can carry the key itself.
      self.mapFail(err && err.message ? err.message : String(err));
    });
  };

  /** Frame the store's own outlets rather than a hardcoded country centre —
   *  a store with three outlets in Sarawak should not open on Kuala Lumpur. */
  Locator.prototype.startCenter = function () {
    if (!isNaN(this.cfg.centerLat) && !isNaN(this.cfg.centerLng)) {
      return { lat: this.cfg.centerLat, lng: this.cfg.centerLng, zoom: this.cfg.zoom };
    }
    if (!this.all.length) return { lat: 4.2, lng: 108.0, zoom: 5 };

    var lats = this.all.map(function (l) { return l.lat; });
    var lngs = this.all.map(function (l) { return l.lng; });
    var minLat = Math.min.apply(null, lats), maxLat = Math.max.apply(null, lats);
    var minLng = Math.min.apply(null, lngs), maxLng = Math.max.apply(null, lngs);
    var span = Math.max(maxLat - minLat, maxLng - minLng);

    var zoom = span > 12 ? 4 : span > 6 ? 5 : span > 3 ? 6 : span > 1 ? 8 : span > 0.3 ? 10 : 12;
    return { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2, zoom: zoom };
  };

  Locator.prototype.flyTo = function (lat, lng, zoom) {
    if (!this.map) return;
    this.map.panTo({ lat: lat, lng: lng });
    if (zoom) this.map.setZoom(zoom);
  };

  /** Frame two points — the place searched and the nearest outlet — so a
   *  shopper in Kuantan sees both Kuantan and the Temerloh pharmacy the
   *  notice names, not an empty map centred on their own town. */
  Locator.prototype.fitTo = function (a, b) {
    if (!this.map || !window.google || !google.maps) return;
    var bounds = new google.maps.LatLngBounds();
    bounds.extend({ lat: a.lat, lng: a.lng });
    bounds.extend({ lat: b.lat, lng: b.lng });
    this.map.fitBounds(bounds, 60);
  };

  /** The town from a display address: the part before the state, with the
   *  country, a bare postcode and a leading postcode stripped — "…, 68100,
   *  Batu Caves, Selangor., Malaysia" gives "Batu Caves". Best effort; an
   *  address that does not fit the pattern yields "" and the line still reads. */
  function townOf(address) {
    var parts = (address || "").split(",").map(function (p) { return p.trim().replace(/[.\s]+$/, ""); })
      .filter(function (p) { return p && !/^malaysia$/i.test(p) && !/^\d{5}$/.test(p); });
    if (!parts.length) return "";
    var town = parts.length >= 2 ? parts[parts.length - 2] : parts[0];
    return town.replace(/^\d{5}\s*/, "");
  }

  /** The line a shopper gets when their place has no stockist but the store
   *  does have one somewhere: names it, and how far. Null when there is
   *  nothing to point at (no origin, or no outlets at all). */
  Locator.prototype.nearestLine = function (placeName) {
    var list = this.current || [];
    var near = list[0];
    if (!near || typeof near.distanceKm !== "number") return null;
    return this.copy.nearest(placeName, near.name, townOf(near.address), near.distanceKm);
  };

  Locator.prototype.clearMarkers = function () {
    for (var i = 0; i < this.markers.length; i++) this.markers[i].setMap(null);
    this.markers = [];
    if (this.info) this.info.close();
  };

  /** Grid clusterer, ported verbatim in spirit from the handover: buckets by
   *  rounded lat/lng at a cell size derived from zoom. Kept in-house rather
   *  than loading a clustering plugin. */
  Locator.prototype.cluster = function (locations) {
    var zoom = this.map ? this.map.getZoom() : 12;
    if (!this.cfg.cluster || zoom >= 11) {
      return locations.map(function (l) { return { lat: l.lat, lng: l.lng, items: [l] }; });
    }

    var cell = (360 / Math.pow(2, zoom)) * 2;
    var buckets = {};
    locations.forEach(function (l) {
      var key = Math.round(l.lat / cell) + ":" + Math.round(l.lng / cell);
      if (!buckets[key]) buckets[key] = [];
      buckets[key].push(l);
    });

    return Object.keys(buckets).map(function (key) {
      var items = buckets[key];
      var lat = items.reduce(function (s, l) { return s + l.lat; }, 0) / items.length;
      var lng = items.reduce(function (s, l) { return s + l.lng; }, 0) / items.length;
      return { lat: lat, lng: lng, items: items };
    });
  };

  Locator.prototype.drawMarkers = function (locations) {
    if (!this.map || !this.maps) return;
    var self = this;
    var maps = this.maps;

    this.clearMarkers();
    var groups = this.cluster(locations || []);

    groups.forEach(function (group) {
      if (group.items.length === 1) {
        var loc = group.items[0];
        var icon;
        if (self.cfg.pinImage) {
          // A store's own logo pin. The image is baked with its tip flush to
          // the bottom-centre, which is the Marker's default anchor — so no
          // anchor override is needed and it points as accurately as the
          // coloured pin.
          icon = { url: self.cfg.pinImage, scaledSize: new maps.Size(self.cfg.pinImageW, self.cfg.pinImageH) };
        } else {
          var color = (self.cfg.pinByType && loc.tags && loc.tags.length && self.tagColors[loc.tags[0]]) || self.cfg.markerColor;
          icon = { url: pinSvg(color), scaledSize: new maps.Size(28, 38) };
        }

        var marker = new maps.Marker({
          position: { lat: loc.lat, lng: loc.lng },
          map: self.map,
          title: loc.name,
          icon: icon,
        });
        marker.addListener("click", function () { self.openPopup(loc, marker); });
        self.markers.push(marker);
        return;
      }

      var count = group.items.length;
      var size = count > 50 ? 52 : count > 10 ? 44 : 38;
      var half = size / 2;
      var clusterMarker = new maps.Marker({
        position: { lat: group.lat, lng: group.lng },
        map: self.map,
        title: self.copy.cluster(count),
        label: { text: String(count), color: "#ffffff", fontSize: "12px", fontWeight: "700" },
        icon: {
          url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(
            "<svg xmlns='http://www.w3.org/2000/svg' width='" + size + "' height='" + size + "'>" +
              "<circle cx='" + half + "' cy='" + half + "' r='" + (half - 1) + "' fill='" +
              self.cfg.clusterColor + "' fill-opacity='0.25'/>" +
              "<circle cx='" + half + "' cy='" + half + "' r='" + (half - 4) + "' fill='" +
              self.cfg.clusterColor + "' fill-opacity='0.92'/></svg>"),
          scaledSize: new maps.Size(size, size),
        },
      });

      clusterMarker.addListener("click", function () {
        var bounds = new maps.LatLngBounds();
        group.items.forEach(function (l) { bounds.extend(new maps.LatLng(l.lat, l.lng)); });
        self.map.fitBounds(bounds, 56);
      });

      self.markers.push(clusterMarker);
    });
  };

  Locator.prototype.openPopup = function (loc, marker) {
    if (!this.map || !this.info) return;

    var kids = [];
    if (this.cfg.showPhotos && safeImage(loc.image)) {
      kids.push(el("img", { class: "sl-popup__img", src: loc.image, alt: "", loading: "lazy" }));
    }
    kids.push(el("h3", { class: "sl-popup__name", text: loc.name }));
    if (loc.tags && loc.tags.length) kids.push(this.tagRow(loc.tags));
    kids.push(el("p", { class: "sl-popup__addr", text: loc.address }));
    var popNote = this.noteFor(loc);
    if (popNote) kids.push(el("p", { class: "sl-popup__note", text: popNote }));
    if (loc.description) kids.push(el("p", { class: "sl-popup__desc", text: loc.description }));
    if (loc.hours) kids.push(el("p", { class: "sl-popup__hours", text: loc.hours }));
    if (loc.phone) {
      kids.push(el("p", {}, [el("a", { href: "tel:" + loc.phone.replace(/[^\d+]/g, ""), text: loc.phone })]));
    }
    if (loc.email) kids.push(el("p", {}, [el("a", { href: "mailto:" + loc.email, text: loc.email })]));
    if (loc.website) {
      kids.push(el("p", {}, [
        el("a", { href: loc.website, target: "_blank", rel: "noopener nofollow", text: this.copy.website }),
      ]));
    }
    if (this.cfg.showDirections) {
      kids.push(el("div", { class: "sl-popup__links" }, [
        el("a", { class: "sl-popup__dir", href: directionsUrl(loc), target: "_blank",
                  rel: "noopener", text: this.cfg.directionsLabel }),
        el("a", { class: "sl-popup__dir sl-popup__dir--waze", href: wazeUrl(loc), target: "_blank",
                  rel: "noopener", text: "Waze" }),
      ]));
    }

    // setContent takes a DOM node, so the no-innerHTML rule holds all the way
    // into Google's own popup.
    this.info.setContent(el("div", { class: "sl-popup", "data-sl-name": loc.name }, kids));
    if (marker) this.info.open({ anchor: marker, map: this.map });
    else {
      this.info.setPosition({ lat: loc.lat, lng: loc.lng });
      this.info.open({ map: this.map });
    }

    // Make the open outlet shareable: the URL now names it, so a WhatsApp
    // message can point a customer at one stockist. replaceState, not a
    // hash assignment, so opening popups never scroll-jumps the page.
    try {
      history.replaceState(null, "", "#o-" + encodeURIComponent(loc.id));
    } catch (e) { /* sandboxed contexts (theme editor) can refuse; fine */ }
  };

  Locator.prototype.focus = function (index) {
    var loc = (this.current || this.all)[index];
    if (!loc) return;
    if (!this.map) {
      // No map yet — bringing it into view starts the lazy load.
      this.el.map.scrollIntoView({ behavior: "smooth", block: "nearest" });
      return;
    }

    this.flyTo(loc.lat, loc.lng, Math.max(this.map.getZoom(), 13));

    var self = this;
    // Wait for the pan to settle, so the pin exists un-clustered at the new
    // zoom before we try to anchor a popup to it.
    this.maps.event.addListenerOnce(this.map, "idle", function () {
      self.drawMarkers(self.current || self.all);
      var marker = null;
      for (var i = 0; i < self.markers.length && !marker; i++) {
        var p = self.markers[i].getPosition();
        if (p && Math.abs(p.lat() - loc.lat) < 1e-6 && Math.abs(p.lng() - loc.lng) < 1e-6) {
          marker = self.markers[i];
        }
      }
      self.openPopup(loc, marker);
    });
  };

  /** `kind` "notice" is the same box in a calmer tone: a fact with a way
   *  forward ("your nearest is 85 km away"), not a failure. The per-store
   *  rescue bar watches this element's `hidden`, so a notice also brings up
   *  the WhatsApp line — which is exactly what a shopper that far away needs. */
  Locator.prototype.showError = function (message, kind) {
    this.el.error.textContent = message;
    this.el.error.classList.toggle("sl-error--notice", kind === "notice");
    this.el.error.hidden = false;
  };

  Locator.prototype.hideError = function () {
    this.el.error.hidden = true;
  };

  /* ---------------------------------------------------------------- */

  function boot() {
    var roots = document.querySelectorAll("[data-store-locator]");
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].dataset.slReady === "1") continue;
      roots[i].dataset.slReady = "1";
      new Locator(roots[i]);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  // The theme editor re-renders sections without a page load.
  document.addEventListener("shopify:section:load", boot);

  // For scripts/smoke-locator.ts and `npm run locator:places`, which replay
  // real searches through this code rather than a copy of it. Nothing on
  // the storefront reads it.
  window.__storeLocatorSearch = { resolve: resolveSearch, norm: normPlace };
  // Same reason: the smoke test checks the copy table here rather than
  // against a transcription of it, so a key added to one language and not
  // the other fails a test instead of rendering "undefined" to a shopper.
  window.__storeLocatorCopy = COPY;
})();
